import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, ReactNode, JSX, Ref } from 'react';
import { useRef } from 'react';
import styles from './IntroColumnText.module.scss';
import { setupTransitionInTimeline } from './IntroColumnText.transitions';
import type { IntroColumnTextProps, IntroColumnTextRef } from './IntroColumnText.data';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import type { HeadingRef, HeadingSize } from '../../atoms/heading/Heading.data';
import {
  MARK_BOLD,
  NODE_HEADING,
  NODE_PARAGRAPH,
  render,
} from 'storyblok-rich-text-react-renderer';
import { isTouch } from '../../../hooks/useIsDesktopDevice';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { getLink } from '../../../util/url.utils';
import { useRouter } from 'next/router';

export default function IntroColumnText({
  canTransitionIn,
  blok,
  ref,
  ...props
}: IntroColumnTextProps & { ref?: Ref<IntroColumnTextRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HeadingRef>(null);
  const router = useRouter();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      canTransitionIn,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        titleRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const mapHeading = (level: number): string => {
    if (level) {
      return `heading-0${level}`;
    }
    return `heading-05`;
  };

  return (
    <div
      ref={elementRef}
      className={classNames(
        styles.introColumnText,
        props.className,
        'grid-gutter max-content-width',
      )}
      {...sbEditable(blok)}
    >
      <div className={classNames(styles.titleHolder)}>
        {typeof blok.title === 'string' && blok.title && (
          <Heading
            ref={titleRef}
            as="h3"
            size="heading-05"
            className={classNames(styles.title)}
            enableChineseTransitionFallback
            canTransitionIn={canTransitionIn}
          >
            {blok.title}
          </Heading>
        )}

        {typeof blok.title !== 'string' &&
          blok.title?.content &&
          render(blok.title, {
            nodeResolvers: {
              [NODE_HEADING]: (children: ReactNode, { level }) => (
                <>
                  <Heading
                    ref={titleRef}
                    as="h3"
                    className={classNames(styles[mapHeading(level)], styles.title)}
                    size={mapHeading(level) as HeadingSize}
                    enableChineseTransitionFallback
                    canTransitionIn={canTransitionIn}
                  >
                    {children}
                    <br />
                  </Heading>
                </>
              ),
            },
          })}
      </div>

      <div className={classNames(styles.content)}>
        {blok.description &&
          render(blok.description, {
            markResolvers: {
              [MARK_BOLD]: (children) => {
                if (typeof children === 'string') {
                  return children
                    .split(' ')
                    .map((split) => <span key={split}>{split} </span>) as unknown as JSX.Element;
                }
                return <span>{children}</span>;
              },
            },
            nodeResolvers: {
              [NODE_PARAGRAPH]: (children: ReactNode) => (
                <>
                  <Text
                    transitionInType={isTouch ? 'none' : 'splitLineRotateIn'}
                    className={classNames(styles.description)}
                    as="p"
                    size="quote"
                    canTransitionIn={canTransitionIn}
                    disableTriggerInOnScroll={false}
                  >
                    {children}
                    <br />
                  </Text>
                </>
              ),
            },
          })}

        <div className={classNames(styles.buttonHolder)}>
          {blok?.buttons?.map((button) => {
            const link = getLink(button.link, router.locale);
            if (link) {
              return (
                <PrimaryButton
                  key={button._uid}
                  className={classNames(styles.button)}
                  link={{ href: link }}
                  label={button.title}
                  canTransitionIn={canTransitionIn}
                  disableTriggerInOnScroll={false}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}
